import { ProjectSchemeInterface } from "ts-interfaces";
import { AnyProperties, trackEvent } from "./analyticsManager";

/**
 * Analytics for the Projects surface (`projectsScheme` — the project folders at
 * /home/projects, NOT the `projects` collection, which stores boards).
 *
 * Every event goes through `trackEvent` only (Userpilot + Customer.io). GA4 is
 * deliberately NOT called here: it will be fed from that single chokepoint by
 * the in-progress fan-out work, so a parallel `trackEventGA` call at each site
 * would just be a second thing to unwind. Params are kept GA4-safe regardless
 * (<=25 per event, strings <=100 chars, no empty-string values) since these
 * payloads are what the chokepoint will forward.
 *
 * No `is_internal` stamp either, for the same reason. Library search sets one
 * (Home/library/useLibrarySearch.ts) because GA4's BigQuery export carried no
 * identity at all; Userpilot and Customer.io both receive `identify()` with the
 * user's email, so internal accounts are already segmentable there. When GA4
 * delivery lands the flag belongs in the chokepoint — applied once for every
 * event rather than re-derived per call site, which would also drag the
 * LoginState import chain into every instrumented module.
 *
 * Naming follows the dominant `trackEvent` convention: Title Case, " - "
 * separator ("Schedule - Item Added", "Library - Search").
 *
 * See docs/analytics-projects.md for the full spec.
 */

/** Bump when the meaning of these events changes, so analysis can segment. */
const INSTRUMENTATION_VERSION = 1;

/** Max length for a GA4 string param value. */
const MAX_PARAM_CHARS = 100;

/**
 * No failure events live here on purpose. A rejected write is an error, and
 * errors belong in Sentry — mixing them into the product-analytics stream
 * muddles both. The create/save/upload catch blocks still `console.error`;
 * wiring them to Sentry is separate work.
 */
export const PROJECT_EVENTS = {
  createStarted: "Project - Create Started",
  created: "Project - Created",

  startWithShown: "Project - Start With Shown",
  startWithChosen: "Project - Start With Chosen",

  editOpened: "Project - Edit Opened",
  updated: "Project - Updated",

  coverUploaded: "Project - Cover Uploaded",

  deleted: "Project - Deleted",
  duplicated: "Project - Duplicated",
} as const;

export type ProjectEvent = (typeof PROJECT_EVENTS)[keyof typeof PROJECT_EVENTS];

/** Where the user came from into a create form. */
export type ProjectEntryPoint =
  | "projects_list"
  | "dashboard_onboarding"
  | "board_file_menu"
  | "direct";

const PROJECT_ENTRY_POINTS: ProjectEntryPoint[] = [
  "projects_list",
  "dashboard_onboarding",
  "board_file_menu",
  "direct",
];

/**
 * Coerce a URL-supplied `?from=` into a known entry point.
 *
 * The value reaches us from the address bar, so anyone can put anything there.
 * Passing it through would let a crafted link mint unbounded new dimension
 * values; an unrecognised one is just a direct arrival.
 */
export function toEntryPoint(value: string | null | undefined) {
  return PROJECT_ENTRY_POINTS.includes(value as ProjectEntryPoint)
    ? (value as ProjectEntryPoint)
    : "direct";
}

/** Which create/edit UI fired the event. */
export type ProjectSurface = "page" | "modal";

/** Which surface owns a cover upload. */
export type CoverSurface =
  | "create_form"
  | "create_modal"
  | "overview"
  | "edit_modal";

/**
 * The single emitter. Stamps the shared context and swallows anything a param
 * builder throws — instrumentation must never break a create or a save.
 * (`trackEvent` already guards its own Userpilot/Customer.io calls.)
 */
export function trackProjectEvent(
  event: ProjectEvent,
  properties: AnyProperties = {},
) {
  try {
    trackEvent(event, {
      ...properties,
      instrumentation_version: INSTRUMENTATION_VERSION,
    });
  } catch (error) {
    console.error("[projectAnalytics] trackProjectEvent", event, error);
  }
}

/** True for a value the user actually filled in. */
const filled = (value: unknown): boolean =>
  typeof value === "string" ? value.trim().length > 0 : !!value;

/**
 * How much optional metadata a project carries, 0-5.
 *
 * The five buckets are type / dates / location / description / cover image.
 * Close to but NOT identical to `richness_num_optional_fields_filled` in
 * analysis/project-usage/key_metrics.json, which counted `phase` instead of
 * `coverImage` (covers didn't exist at that snapshot) — see the doc.
 *
 * Currency and measurement unit are excluded on purpose: they are seeded from
 * the profile (#3593), so they are never meaningfully "unfilled".
 */
export function countOptionalFields(
  project: Partial<ProjectSchemeInterface> | null | undefined,
): number {
  if (!project) return 0;
  const buckets = [
    filled(project.type),
    filled(project.startDate) || filled(project.estCompletionDate),
    filled(project.location),
    filled(project.description),
    filled(project.coverImage),
  ];
  return buckets.filter(Boolean).length;
}

/** Every user-editable field on a project doc, in a stable order. */
export const EDITABLE_PROJECT_FIELDS: (keyof ProjectSchemeInterface)[] = [
  "name",
  "type",
  "phase",
  "startDate",
  "estCompletionDate",
  "location",
  "description",
  "size",
  "budget",
  "currency",
  "measurementUnit",
  "coverImage",
];

/**
 * Which editable fields actually changed.
 *
 * Needed because `editProjectScheme` writes the WHOLE document — the payload
 * alone never says what the user touched. Diff against the pre-edit snapshot.
 */
export function diffProjectFields(
  before: Partial<ProjectSchemeInterface> | null | undefined,
  after: Partial<ProjectSchemeInterface> | null | undefined,
): string[] {
  if (!before || !after) return [];
  return EDITABLE_PROJECT_FIELDS.filter((field) => {
    // Treat undefined/null/"" as the same "unset" so a legacy doc that lacks a
    // field doesn't read as changed when the form round-trips it as "".
    const a = before[field] ?? "";
    const b = after[field] ?? "";
    return a !== b;
  }) as string[];
}

/** Join field names into a GA4-safe CSV. */
export function fieldsChangedCsv(fields: string[]): string {
  return (fields || []).join(",").slice(0, MAX_PARAM_CHARS);
}

/** Days since creation. `projectsScheme.createdAt` is epoch-ms, not a Timestamp. */
export function ageDays(createdAt: number | null | undefined): number {
  if (!createdAt || typeof createdAt !== "number") return 0;
  const days = (Date.now() - createdAt) / 86_400_000;
  return days > 0 ? Math.round(days) : 0;
}

/**
 * What a project actually holds, for the delete event.
 *
 * **Boards and collections only, deliberately.** Both are expanded onto the doc
 * by the projects store from project-scoped queries, so both are complete.
 *
 * Concepts and schedules are NOT counted, even though the delete surface has
 * both lists mounted for its card thumbnails. `useConcepts` is capped at
 * `CONCEPTS_WINDOW_SIZE` (100) with the archive filter applied *after* the
 * limit (docs/project-oriented-ui-followups.md), and `useUserSchedules` is an
 * SWR cache that starts empty — so both under-report, and both under-report in
 * the same direction. Including them biased `was_empty` toward `true`, which
 * would have inflated "throwaway creates" on precisely the question this event
 * exists to answer. A count that is wrong in a known direction is worse than no
 * count. Restoring them needs the project-scoped concept query and its two
 * composite indexes, which exist in neither staging nor production.
 */
export function projectContentCounts(
  project: Partial<ProjectSchemeInterface> | null | undefined,
): AnyProperties {
  const boardCount = (project?.boards || []).filter(
    (b: any) => !b?.archive,
  ).length;
  const collectionCount = (project?.collections || []).length;
  return {
    board_count: boardCount,
    collection_count: collectionCount,
    was_empty: boardCount + collectionCount === 0,
  };
}

/**
 * Shape of a project for the created/updated events. `has_*` booleans stay
 * booleans (never ""), so GA4 keeps them.
 */
export function projectShapeProps(
  project: Partial<ProjectSchemeInterface> | null | undefined,
): AnyProperties {
  const name = (project?.name || "").trim();
  const props: AnyProperties = {
    has_type: filled(project?.type),
    has_start_date: filled(project?.startDate),
    has_end_date: filled(project?.estCompletionDate),
    has_description: filled(project?.description),
    has_cover_image: filled(project?.coverImage),
    optional_fields_filled: countOptionalFields(project),
    name_length: name.length,
  };
  // Omit rather than send "" — GA4 drops empty strings anyway, and absent is
  // the honest reading for "the user never picked one".
  if (filled(project?.type)) props.project_type = project?.type;
  if (filled(project?.currency)) props.currency = project?.currency;
  if (filled(project?.measurementUnit)) {
    props.measurement_unit = project?.measurementUnit;
  }
  return props;
}
